/**
 * Procedural "grim Liverpool" ambient soundscape via Web Audio:
 *   - low city drone (60 Hz hum + faint hi-hum) — distant motorway/industry
 *   - filtered brown noise — steady drizzle
 *   - filtered white noise with slow gain modulation — gusts of wind
 *   - sparse seagull caws and very occasional distant car honks
 *
 * No audio assets shipped; everything is synthesised in-browser.
 * Requires a user gesture before start() (handled by Play button click).
 */
class AmbientImpl {
  private ctx?: AudioContext;
  private master?: GainNode;
  private timers: number[] = [];
  private nodes: { stop?: (when?: number) => void }[] = [];
  private running = false;

  start() {
    if (this.running) return;
    if (typeof window === 'undefined' || !window.AudioContext) return;
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor();
    if (ctx.state === 'suspended') void ctx.resume();
    this.ctx = ctx;

    const master = ctx.createGain();
    master.gain.value = 0.35;  // overall ambient bus
    master.connect(ctx.destination);
    this.master = master;

    // --- City drone (60 Hz hum + faint upper harmonic) ---
    const hum = ctx.createOscillator();
    hum.type = 'sawtooth';
    hum.frequency.value = 60;
    const humGain = ctx.createGain();
    humGain.gain.value = 0.22;
    const humLP = ctx.createBiquadFilter();
    humLP.type = 'lowpass';
    humLP.frequency.value = 220;
    hum.connect(humLP).connect(humGain).connect(master);
    hum.start();
    this.nodes.push(hum);

    const upper = ctx.createOscillator();
    upper.type = 'sine';
    upper.frequency.value = 220;
    const upperGain = ctx.createGain();
    upperGain.gain.value = 0.05;
    upper.connect(upperGain).connect(master);
    upper.start();
    this.nodes.push(upper);

    // --- Brown noise rain ---
    const rainBuf = this.makeBrownNoise(ctx, 4);
    const rainSrc = ctx.createBufferSource();
    rainSrc.buffer = rainBuf;
    rainSrc.loop = true;
    const rainLP = ctx.createBiquadFilter();
    rainLP.type = 'lowpass';
    rainLP.frequency.value = 2200;
    const rainGain = ctx.createGain();
    rainGain.gain.value = 0.20;  // pulled down so other layers cut through
    rainSrc.connect(rainLP).connect(rainGain).connect(master);
    rainSrc.start();
    this.nodes.push(rainSrc);

    // --- Wind: filtered white noise with slow gain modulation for gusts ---
    const windBuf = this.makeWhiteNoise(ctx, 6);
    const windSrc = ctx.createBufferSource();
    windSrc.buffer = windBuf;
    windSrc.loop = true;
    const windBP = ctx.createBiquadFilter();
    windBP.type = 'bandpass';
    windBP.frequency.value = 700;
    windBP.Q.value = 0.6;
    const windGain = ctx.createGain();
    windGain.gain.value = 0.0;
    windSrc.connect(windBP).connect(windGain).connect(master);
    windSrc.start();
    this.nodes.push(windSrc);

    // Modulate wind gain via slow LFO for gust feel
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.08;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.14;
    const windOffset = ctx.createConstantSource();
    windOffset.offset.value = 0.18;
    windOffset.start();
    lfo.connect(lfoGain).connect(windGain.gain);
    windOffset.connect(windGain.gain);
    lfo.start();
    this.nodes.push(lfo, windOffset);

    // --- Periodic seagulls (sparse, off-screen) ---
    this.scheduleGull();
    // --- Periodic distant honks ---
    this.scheduleHonk();

    this.running = true;
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    for (const id of this.timers) clearTimeout(id);
    this.timers = [];
    for (const n of this.nodes) { try { n.stop?.(); } catch (e) { /* already stopped */ } }
    this.nodes = [];
    void this.ctx?.close();
    this.ctx = undefined;
    this.master = undefined;
  }

  /** Master volume gain 0..1 */
  setVolume(v: number) {
    if (this.master) this.master.gain.value = Math.max(0, Math.min(1, v));
  }

  private scheduleGull() {
    if (!this.running || !this.ctx) return;
    const delay = 3500 + Math.random() * 8000; // 3.5–11.5 s
    const id = window.setTimeout(() => {
      this.gullCaw();
      this.scheduleGull();
    }, delay);
    this.timers.push(id);
  }

  private scheduleHonk() {
    if (!this.running || !this.ctx) return;
    const delay = 5000 + Math.random() * 12000; // 5–17 s
    const id = window.setTimeout(() => {
      this.distantHonk();
      this.scheduleHonk();
    }, delay);
    this.timers.push(id);
  }

  private gullCaw() {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime;
    const cawCount = 1 + Math.floor(Math.random() * 3);
    for (let i = 0; i < cawCount; i++) {
      const start = t0 + i * 0.22;
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(720 + Math.random() * 200, start);
      o.frequency.linearRampToValueAtTime(420 + Math.random() * 120, start + 0.18);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0, start);
      g.gain.linearRampToValueAtTime(0.18, start + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.24);
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 1400;
      bp.Q.value = 1.5;
      o.connect(bp).connect(g).connect(this.master);
      o.start(start);
      o.stop(start + 0.25);
    }
  }

  private distantHonk() {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = 220 + Math.random() * 80;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0, t0);
    g.gain.linearRampToValueAtTime(0.12, t0 + 0.04);
    g.gain.setValueAtTime(0.12, t0 + 0.32);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.45);
    // muffle as if far away
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 600;
    o.connect(lp).connect(g).connect(this.master);
    o.start(t0);
    o.stop(t0 + 0.5);
  }

  private makeBrownNoise(ctx: AudioContext, seconds: number): AudioBuffer {
    const len = ctx.sampleRate * seconds;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.5;
    }
    return buf;
  }

  private makeWhiteNoise(ctx: AudioContext, seconds: number): AudioBuffer {
    const len = ctx.sampleRate * seconds;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }
}

export const Ambient = new AmbientImpl();
